import {useEffect, useState} from 'react';
import {gql} from '@apollo/client';
import {useRouter} from 'next/router';

import {surveyWizardVars} from '#apollo/local/SurveyVar';
import {QuestionTypeEnum, useSurveyQuery, useSurveySubmission} from '#apollo/schema';
import {surveyQuestionFragment} from '#component/partial/Wizard/component/Question/Question';
import {SURVEY_KEY} from '#constant/survey';

const surveyInput = {
  input: {key: SURVEY_KEY},
} as const;

export const surveySubmissionMutation = gql`
  mutation SurveySubmission($input: SaveUserSurveySubmissionInput!) {
    saveUserSurveySubmission(input: $input) {
      id
      user {
        id
        mostRecentLabSubmission {
          id
          surveySubmissionId
          userSurveySubmission {
            id
          }
        }
      }
    }
  }
`;

const answerKeyMap = {
  [QuestionTypeEnum.MultipleChoiceEnum]: 'answerOptionIds',
  [QuestionTypeEnum.NumericEnum]: 'answerNumeric',
  [QuestionTypeEnum.ShortTextEnum]: 'answerText',
  [QuestionTypeEnum.LongTextEnum]: 'answerText',
} as const;

export const surveyQuery = gql`
  query SurveyQuery($input: GetSurveyInput!) {
    me {
      id
      mostRecentLabSubmission {
        id
        userSurveySubmission {
          id
        }
      }
      mostRecentLabSubmissionWithResults {
        id
      }
    }
    survey(input: $input) {
      id
      key
      categories {
        id
        description
        key
        title
        steps {
          id
          stepType
          question {
            ...SurveyQuestionFragment
          }
        }
      }
    }
  }

  ${surveyQuestionFragment}
`;

export const useSurvey = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [surveySubmission] = useSurveySubmission();
  const {data, loading: queryLoading, previousData} = useSurveyQuery({variables: surveyInput});
  const {push} = useRouter();
  const {survey, me} = data || {};
  const {mostRecentLabSubmission, mostRecentLabSubmissionWithResults} = me || {};
  const {id} = mostRecentLabSubmission?.userSurveySubmission || {};
  const didFirstLoad = !queryLoading && !previousData;

  // If the user either:
  // - has no `mostRecentLabSubmission` or
  // - the `mostRecentLabSubmission` already has an associated survey submission
  // ... then they cannot take the survey, so redirect them away from the page.
  const canTakeSurvey = !!mostRecentLabSubmission && !id;

  const handleSubmitSurvey = async (onSubmitted?: () => Promise<void>) => {
    if (survey) {
      // Note: We don't need to reset it to false because the component will unmount when we
      // transition and then start with the initial value when re-mounted
      setIsLoading(true);

      const surveyAnswers = surveyWizardVars.read()[survey.id];

      await surveySubmission({
        variables: {
          input: {
            surveyId: survey.id,
            surveyKey: survey.key,
            answers: Object.keys(surveyAnswers || {}).map(key => {
              const {answer, ...rest} = surveyAnswers[key];
              return {...rest, [answerKeyMap[rest.questionType]]: answer};
            }),
            labSubmissionId: mostRecentLabSubmission?.id,
          },
        },
      });

      surveyWizardVars.reset();

      if (onSubmitted) {
        await onSubmitted();
      }

      if (!!mostRecentLabSubmissionWithResults?.id) {
        await push('/lifestylesummary');
      } else {
        await push('/lifestylepreview');
      }
    } else {
      throw new Error('Missing survey information!');
    }
  };

  useEffect(() => {
    if (didFirstLoad && !canTakeSurvey) {
      push('/dashboard');
    }
  }, [canTakeSurvey, didFirstLoad, push]);

  const categories = survey?.categories.map(category => category.title);

  return {
    categories,
    handleSubmit: handleSubmitSurvey,
    loading: queryLoading || isLoading || !canTakeSurvey,
    survey: survey,
  };
};
